'use client';

import { Button } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { Settings } from '@shared/icons';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useState, type ReactNode } from 'react';

const debug = createDebug({ isEnabled: false });

type GenericErrorStepProps = {
  onAction?: () => void | Promise<void>;
  actionHref?: string;
  errorTitle?: string;
  errorMessage?: string;
  actionText?: string;
  retryingText?: string;
  translationNamespace?: string;
  /** Optional icon area; defaults to the settings illustration. */
  illustration?: ReactNode;
  /** Overrides the default analytics label for the primary button. */
  actionButtonTrackName?: string;
  /** Analytics container name for the error screen. */
  trackContainer?: string;
  /** Analytics name for the error title; omitted when unset. */
  titleTrackName?: string;
  /** Analytics name for the error description. */
  descriptionTrackName?: string;
};

function GenericErrorStep({
  onAction,
  actionHref,
  errorTitle,
  errorMessage,
  actionText,
  retryingText,
  translationNamespace = 'register.screens.error',
  illustration,
  actionButtonTrackName,
  trackContainer = 'Failure',
  titleTrackName,
  descriptionTrackName = 'Reason',
}: GenericErrorStepProps) {
  const t = useTranslations(translationNamespace);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleAction = async () => {
    if (!onAction) {
      return;
    }

    setIsProcessing(true);
    try {
      await onAction();
    } catch (error) {
      debug.forceError('Error during action:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const displayTitle = errorTitle ?? t('title');
  const displayMessage = errorMessage ?? t('description');
  const displayActionText = actionText ?? t('goBack');
  const displayRetryingText = retryingText ?? t('retrying');
  const defaultTrackName = actionText ? 'Help Centre' : 'Back';
  const buttonTrackName = actionButtonTrackName ?? defaultTrackName;

  return (
    <div
      className="w-full flex flex-col flex-1 items-center justify-center self-center gap-5 text-center p-6 md:p-8 md:max-w-2xl"
      data-track-container={trackContainer}
      data-testid="error-step"
    >
      {illustration ? <span data-testid="error-illustration">{illustration}</span> : <Settings size="extra-large" />}
      <h4 className="md:heading-3" data-track-name={titleTrackName} data-testid="error-title">
        {displayTitle}
      </h4>
      <p className="paragraph" data-track-name={descriptionTrackName} data-testid="error-message">
        {displayMessage}
      </p>
      <div className="w-full md:w-auto" data-track-container="Actions">
        {actionHref ? (
          <Button asChild className="w-full md:w-auto" data-testid="button" data-track-name={buttonTrackName}>
            <Link href={actionHref} target="_blank" rel="noopener noreferrer">
              {displayActionText}
            </Link>
          </Button>
        ) : (
          <Button type="button" onClick={handleAction} disabled={isProcessing} className="w-full md:w-auto" data-testid="button" data-track-name={buttonTrackName}>
            {isProcessing ? displayRetryingText : displayActionText}
          </Button>
        )}
      </div>
    </div>
  );
}

export { GenericErrorStep, type GenericErrorStepProps };
